import {
  PrimaryObjectUploadStep,
  SmartConnectorBase,
  SmartConnectorFlow,
  SmartConnectorStep,
} from './types';

// we do not support types other than spreadsheet, webhook and schedule for now
// polling and direct_api are placeholders for future development
// and can be changed in future
export const SMART_CONNECTOR_TYPES = {
  spreadsheet: 'spreadsheet',
  webhook: 'webhook',
  schedule: 'schedule',
  bulkaction: 'bulkaction',
  activity: 'activity',
  polling: 'polling',
  direct_api: 'direct_api',
} as const;

export const SMART_CONNECTOR_STATUSES = {
  setup: 'setup',
  operational: 'operational',
  need_attention: 'need_attention',
  inactive: 'inactive',
} as const;

export const SMART_CONNECTOR_STEPS = {
  general: 'general',
  connection: 'connection',
  variables: 'variables',
} as const;

export const PRIMARY_OBJECT_UPLOAD_STEPS = {
  matching: 'matching',
  mapping: 'mapping',
  preview: 'preview',
} as const;

export const SMARTCONNECTORS_PATHNAME = '/smartconnectors';

export const SMARTCONNECTORS_S3_SOURCES = {
  IMPORT: 'smart_connector_import',
  EXPORT: 'smart_connector_export',
} as const;

export const SQL_SCRIPT_STATUSES = {
  template: 'template',
  draft: 'draft',
  live: 'live',
} as const;

export const SQL_SCRIPT_STATES = {
  setup: 'setup',
  queued: 'queued',
  in_progress: 'in_progress',
  failed: 'failed',
  success: 'success',
} as const;

export const STEP_DATA_BY_STEP: Record<
  SmartConnectorStep,
  (keyof SmartConnectorBase)[]
> = {
  [SMART_CONNECTOR_STEPS.general]: [
    'name',
    'api_name',
    'description',
    'connector_type',
    'custom_object',
    'error_notification_email',
    'all_notification_email',
    'is_diff_check_enabled',
    'variables_to_exclude_from_diffs',
    'max_concurrent_executions',
    'cadence',
    'schedule_rrule',
    'schedule_is_advanced',
    'bulkaction_fields',
    'activity_object',
    'activity_fields',
  ],
  [SMART_CONNECTOR_STEPS.connection]: [
    'sql_required',
    'sql_enabled',
    'source_file',
    'last_draft_script',
    'live_script',
  ],
  [SMART_CONNECTOR_STEPS.variables]: ['execution_variables', 'headers'],
};

export const PROPS_THAT_NEED_DEACTIVATION_ON_CHANGE = [
  'connector_type',
  'sql_enabled',
  'source_file',
  'live_script',
  'execution_variables',
  'bulkaction_fields',
  'activity_fields',
  'activity_object',
] as (keyof SmartConnectorBase)[];

export const PRIMARY_OBJECT_UPLOAD_STEP_DATA_BY_STEP: Record<
  PrimaryObjectUploadStep,
  (keyof SmartConnectorFlow)[]
> = {
  [PRIMARY_OBJECT_UPLOAD_STEPS.matching]: ['loads'],
  [PRIMARY_OBJECT_UPLOAD_STEPS.mapping]: ['loads'],
  [PRIMARY_OBJECT_UPLOAD_STEPS.preview]: ['loads'],
};

export const VARIABLE_TYPES = {
  additional: 'additional',
  related_object_upload: 'related_object_upload',
  data_source: 'data_source',
} as const;

export const RULE_ACTIONS = {
  create_new: 'create_new',
  next_rule: 'next_rule',
  do_not_upload: 'do_not_upload',
  update_current: 'update_current',
  next_rule_ignore_previous: 'next_rule_ignore_previous',
  unarchive_and_update: 'unarchive_and_update',
  unarchive_only: 'unarchive_only',
};

export const CONFLICT_RESOLUTIONS = {
  only_update_blank: 'only_update_blank',
  only_add_options: 'only_add_options',
  overwrite: 'overwrite',
  overwrite_skip_if_blank: 'overwrite_skip_if_blank',
} as const;

export const SMART_FIELD_TYPES = {
  checkbox: 'checkbox',
  checkboxes: 'checkboxes',
  date: 'date',
  datetime: 'datetime',
  dropdown: 'dropdown',
  dynamictags: 'dynamictags',
  email: 'email',
  decimal: 'decimal',
  files: 'files',
  integer: 'integer',
  phonenumber: 'phonenumber',
  money: 'money',
  radio: 'radio',
  rating: 'rating',
  relationship: 'relationship',
  status: 'status',
  text: 'text',
  longtext: 'longtext',
  yesnomaybe: 'yesnomaybe',
  team_selector: 'team_selector',
  timezone: 'timezone',
  kizen_id: 'kizen_id',
  team_associations: 'team_associations',
} as const;

export const ALLOWED_SOURCE_FILE_EXTENSIONS = [
  '.csv',
  '.tsv',
  '.xls',
  '.xlsx',
  '.xlsm',
  '.xlsb',
  '.xla',
  '.xlam',
  '.ods',
  '.zip',
];

export const ALLOWED_WEBHOOK_SOURCE_FILE_EXTENSIONS = ['.csv', '.zip'];
export const ALLOWED_SCHEDULE_SOURCE_FILE_EXTENSIONS = ['.csv', '.zip'];
export const ALLOWED_BULKACTION_SOURCE_FILE_EXTENSIONS = ['.csv', '.zip'];
export const ALLOWED_ACTIVITY_SOURCE_FILE_EXTENSIONS = ['.csv', '.zip'];

export const KIZEN_ID_OPTION = {
  value: SMART_FIELD_TYPES.kizen_id,
  label: 'Kizen ID',
};

export const SMART_CONNECTOR_EXECUTION_STATUSES = {
  setup: 'setup',
  queued: 'queued',
  in_progress: 'in_progress',
  failed: 'failed',
  success: 'success',
  cancelled: 'cancelled',
  waiting: 'waiting',
} as const;

export const SMART_CONNECTOR_EXECUTION_STEP_STATUSES = {
  queued: 'queued',
  in_progress: 'in_progress',
  completed: 'completed',
  failed: 'failed',
};

export const SMART_CONNECTOR_EXECUTION_STEPS = {
  sql_run: 'sql_run',
  execution_variables: 'execution_variables',
  load_step: 'load_step',
};

export const AUTOMATION_TRIGGER_CONFIGS = {
  fire_all: 'fire_all',
  suppress_all: 'suppress_all',
  suppress_record_created: 'suppress_record_created',
  suppress_field_updated: 'suppress_field_updated',
} as const;

export const MAX_VARIABLE_NAME_LENGTH = 100;
export const MIN_VARIABLE_NAME_LENGTH = 1;

export const SMART_CONNECTOR_LOAD_TYPES = {
  csv_load: 'csv_load',
  run_smartconnector: 'run_smartconnector',
} as const;

export const ERROR_HANDLING_ON_ERROR_OPTIONS = {
  error: 'error',
  ignore: 'ignore',
} as const;

export const ERROR_HANDLING_IF_INACTIVE_OPTIONS = {
  queue: 'queue',
  skip: 'skip',
  error: 'error',
} as const;

export const ERROR_HANDLING_ON_ZERO_RECORDS_OPTIONS = {
  skip: 'skip',
  error: 'error',
} as const;

export const ERROR_HANDLING_ON_CHILD_CANCELLED_OPTIONS = {
  cancel: 'cancel',
  continue: 'continue',
} as const;

export const INCLUDED_RECORDS_OPTIONS = {
  created: 'created',
  updated: 'updated',
} as const;

export const DRY_RUN_EXECUTION_SEARCH_PARAM = 'dry_run';
export const EXECUTION_ID_SEARCH_PARAM = 'execution_id';

export const RUN_SMARTCONNECTOR_OUTCOME_STATUSES = {
  child_started: 'child_started',
  child_succeeded: 'child_succeeded',
  child_failed: 'child_failed',
  child_failed_ignored: 'child_failed_ignored',
  child_cancelled: 'child_cancelled',
  child_cancelled_ignored: 'child_cancelled_ignored',
  child_inactive: 'child_inactive',
  child_deleted: 'child_deleted',
  zero_records: 'zero_records',
  input_error: 'input_error',
  skipped_child_inactive: 'skipped_child_inactive',
  skipped_zero_records: 'skipped_zero_records',
};
